/**
 * Activity / Sources - which stores each third-party feed covers (release 3.6
 * item 21, RFP TL-08). Reads GET /v1/activity/feeds/coverage: coverage is what
 * a feed actually delivered AND we ingested inside the window, so a store with
 * no recent event is shown as a gap, and a store name we cannot match to the
 * catalogue is shown as unmatched instead of being counted. A viewer limited to
 * some business units sees only their stores and no unmatched names; a feed's
 * last event and "overdue" are the feed's own for every viewer (round 10).
 */

import { useEffect, useState } from 'react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  getActivityFeedCoverage,
  type ActivityFeedCoverage,
  type ActivityFeedPiiElements,
  type ActivityOwnSensor,
  type ActivityOwnSensorCoverage,
} from '@/lib/api';

const VENDOR_LABEL: Record<string, string> = { zscaler: 'Zscaler', third_party_dam: 'Existing DAM' };

/**
 * Why delivered events of a store name are not in the activity pipeline, one
 * true cause per reason with its remedy (round 10). Round 11: a resend is
 * taken again only when the credential that SENDS it can see the store, so
 * every text that promises a resend says so, and the report tells an
 * all-units viewer, per feed and name, whether the delivering credential can
 * see the store (matched_after_delivery), cannot (matched_outside_sender_units)
 * or is not known (matched_sender_scope_unknown). Pinned by
 * feed-coverage.test.tsx; the module README "Feed operations" says the same.
 */
export type UnmatchedReason =
  | 'not_in_catalogue'
  | 'ambiguous'
  | 'matched_after_delivery'
  | 'matched_outside_sender_units'
  | 'matched_sender_scope_unknown'
  | 'outside_sender_units';

export const UNMATCHED_REASON_TEXT: Record<UnmatchedReason, string> = {
  not_in_catalogue:
    'Not in the catalogue: no store has this name, id or external reference, so these events are not in the activity pipeline. Catalogue the store (or add this name to it as an external reference), then have the feed send these events again within 7 days of delivery. The resend is ingested only if the name then matches one store that the feed credential can see: if that credential is limited to business units, the store must be in one of them, otherwise the resend is skipped as a duplicate (give the credential that business unit; do not move the store).',
  ambiguous:
    "This name matches more than one catalogued store, so these events are not in the activity pipeline. Have the feed name the store by its asset id, or give the store a unique external reference, then have the feed send these events again within 7 days of delivery. If the feed credential is limited to business units, it must be able to see that store's business unit, otherwise the resend is skipped as a duplicate (give the credential that business unit; do not move the store).",
  matched_after_delivery:
    'Not yet in the catalogue when these events arrived: the name matches one store now, but did not match exactly one store at delivery (the store was catalogued, renamed or made unique afterwards), and the feed credential that delivered these events can see that store. Have the feed send these events again within 7 days of delivery with the same credential: the resend is matched again and ingested. If that credential has lost the business unit of the store since, the resend is skipped; give it that business unit again.',
  matched_outside_sender_units:
    'The name matches one store now, but that store is in a business unit the feed credential that delivered these events cannot see, so a resend with that credential is skipped as a duplicate and these events are not ingested. If this feed is meant to report on that store, give the feed credential that business unit (do not move the store), then have the feed send these events again within 7 days of delivery. If it is not meant to, nothing is wrong.',
  matched_sender_scope_unknown:
    "The name matches one store now, but did not match exactly one store when these events arrived, and it is not known which business units the credential that resends them can see (these events came with more than one credential, or before credentials were recorded). Have the feed send these events again within 7 days of delivery. If the feed credential sees every business unit, or sees this store's business unit, the resend is ingested; if it is limited to business units that do not include this store's, the resend is skipped as a duplicate: give the credential that business unit (do not move the store).",
  outside_sender_units:
    'Sent with a feed credential that cannot see the business unit of the one store this name matches, so these events were not ingested, and a resend with that credential is skipped as a duplicate. If this feed is meant to report on that store, give the feed credential that business unit (do not move the store), then have the feed send these events again within 7 days of delivery. If it is not meant to, nothing is wrong.',
};

/** Shown to a viewer limited to some business units instead of the list (round 11: conditional, names no other unit). */
export const UNMATCHED_HIDDEN_TEXT =
  "Store names a feed sent that match no catalogued store, or several, are shown only to users who can see every business unit. If a feed credential is limited to business units, it must be able to see a store's business unit for that store's events to be ingested, on first delivery or on a resend; otherwise the resend is skipped as a duplicate. A user who sees every business unit can see which names are affected.";

function reasonText(reason: string): string {
  return (UNMATCHED_REASON_TEXT as Record<string, string>)[reason] ?? reason;
}

function interval(minutes: number): string {
  return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : 'never';
}

/** T-21: what each of FOCTTA's own sensors is, as the card names it. */
export const OWN_SENSOR_LABEL: Record<ActivityOwnSensor, string> = {
  dam_collector: 'FOCTTA DAM collector',
  ddr_gateway: 'FOCTTA decision gateway',
  discovery_scan: 'FOCTTA scan',
};

/**
 * Shown next to the totals whenever an activity sensor could not be read in
 * full, whether or not any gap is listed (round 2): the "watched by nothing"
 * figure may count stores that are covered.
 */
export const OWN_GAPS_INCOMPLETE_TEXT =
  'Some activity sensors could not be read in full (see below), so the "watched by nothing" figure and list may include stores that are covered. Do not treat them as final until the activity store answers.';

/** Round 2: why the decision gateway credits no store. */
export const DDR_NOT_CREDITED_TEXT =
  'not credited to any store: the gateway records only the bare table name, not the database or schema, so a call cannot be tied to one catalogued store';

/** T-21: a feed's or store's PII elements in one line; unmapped names are a count only. */
export function piiElementsText(p: ActivityFeedPiiElements | undefined): string {
  if (!p || (p.elements.length === 0 && p.unmapped.names === 0)) return 'none reported';
  const parts = p.elements.map((e) => `${e.element} (${e.events})`);
  if (p.unmapped.names > 0) {
    // Name occurrences (each event's distinct names, summed), not distinct names.
    parts.push(
      `${p.unmapped.names} unmapped name${p.unmapped.names === 1 ? '' : 's'} in ${p.unmapped.events} event${p.unmapped.events === 1 ? '' : 's'}`,
    );
  }
  return parts.join(', ');
}

function hours(h: number): string {
  return h % 24 === 0 ? `${h / 24} d` : `${h} h`;
}

/**
 * T-21: stores covered by FOCTTA's own sensors next to the feeds. A scan is
 * posture, not activity monitoring, so it never makes a store activity-covered.
 */
export function OwnSensorCoverage({ own, allUnits }: { own: ActivityOwnSensorCoverage; allUnits: boolean }) {
  return (
    <div className="space-y-3" data-testid="own-sensor-coverage">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">FOCTTA's own sensors</h2>
      <p className="text-sm" data-testid="own-sensor-totals">
        {own.totals.activityCoveredAny} of {own.totals.assets} catalogued stores have activity watched by a feed or by FOCTTA (
        {own.totals.ownActivityCovered} by FOCTTA's own sensors); <strong>{own.totals.activityGaps} watched by nothing</strong>.{' '}
        {own.totals.scanned} scanned recently (a scan is not activity monitoring).
      </p>
      {!own.totals.gapsComplete && (
        <p className="text-xs text-destructive" data-testid="own-sensor-gaps-incomplete">{OWN_GAPS_INCOMPLETE_TEXT}</p>
      )}
      <ul className="text-sm">
        {own.sensors.map((s) => (
          <li key={s.sensor} data-testid={`own-sensor-${s.sensor}`}>
            {OWN_SENSOR_LABEL[s.sensor]} (last {hours(s.windowHours)}):{' '}
            {s.creditsStores === false ? (
              <span data-testid={`own-sensor-${s.sensor}-not-credited`}>{DDR_NOT_CREDITED_TEXT}</span>
            ) : s.available ? (
              <>
                {s.truncated ? 'at least ' : ''}
                {s.storesCovered} {allUnits ? 'stores' : 'of your stores'}
              </>
            ) : (
              <Badge variant="destructive">
                not available ({s.unavailableReason === 'not_configured' ? 'no activity store configured' : 'activity store unreachable'})
              </Badge>
            )}
          </li>
        ))}
      </ul>
      {own.ddrUnplacedTables && own.ddrUnplacedTables.uncatalogued + own.ddrUnplacedTables.nameOnlyNotCredited > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="own-sensor-ddr-unplaced">
          The decision gateway was asked about {own.ddrUnplacedTables.nameOnlyNotCredited} table name(s) that match a catalogued
          store ({own.ddrUnplacedTables.nameOnlyCalls} calls; name only, not credited) and {own.ddrUnplacedTables.uncatalogued}{' '}
          name(s) no catalogued store has. None of them counts as coverage here.
        </p>
      )}
      {own.stores.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="own-sensor-stores">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Store</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Activity</th>
                <th className="text-left px-3 py-2">Seen by</th>
              </tr>
            </thead>
            <tbody>
              {own.stores.items.map((s) => (
                <tr key={s.assetId}>
                  <td className="px-3 py-1">{s.displayName}</td>
                  <td className="px-3 py-1 text-xs">{s.sourceType}</td>
                  <td className="px-3 py-1">
                    <Badge variant={s.activityCovered ? 'success' : 'destructive'}>{s.activityCovered ? 'watched' : 'not watched'}</Badge>
                  </td>
                  <td className="px-3 py-1 text-xs">
                    {s.sensors
                      .map((x) => `${OWN_SENSOR_LABEL[x.sensor]}${x.count !== null ? ` (${x.count})` : ''}, last ${when(x.lastSeenAt)}`)
                      .concat(s.coveredByFeed ? ['a third-party feed'] : [])
                      .join('; ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {own.stores.truncated && <p className="text-xs text-muted-foreground mt-1">Showing the first {own.stores.items.length}.</p>}
        </div>
      )}
      {own.activityGaps.items.length > 0 && (
        <div data-testid="own-sensor-gaps">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
            Activity watched by nothing ({own.totals.activityGaps})
          </h3>
          <ul className="text-sm columns-2">
            {own.activityGaps.items.map((g) => (
              <li key={g.assetId}>
                {g.displayName} <span className="text-xs text-muted-foreground">({g.sourceType}{g.scanned ? ', scanned' : ''})</span>
              </li>
            ))}
          </ul>
          {own.activityGaps.truncated && (
            <p className="text-xs text-muted-foreground mt-1">Showing the first {own.activityGaps.items.length}.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function FeedCoverageCard({ refreshKey }: { refreshKey?: unknown }) {
  const [report, setReport] = useState<ActivityFeedCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getActivityFeedCoverage()
      .then((r) => {
        if (alive) {
          setReport(r);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : 'coverage failed to load');
      });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  if (error) {
    return (
      <section className="mt-8" data-testid="feed-coverage">
        <p className="text-sm text-destructive">Feed coverage could not be loaded: {error}</p>
      </section>
    );
  }
  if (!report) return null;

  return (
    <section className="mt-8 space-y-4" data-testid="feed-coverage">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
        Third-party feed coverage (last {report.withinHours} h)
      </h2>

      {report.feeds.length === 0 ? (
        <GlassCard className="p-6 text-sm text-muted-foreground">
          No third-party feed yet. Add a source with capture mode <strong>Third-party feed</strong>, then have Zscaler or
          your DAM post events to <code>/v1/activity/feeds/&lt;source row id&gt;/events</code>.
        </GlassCard>
      ) : (
        <>
          <p className="text-sm" data-testid="feed-coverage-totals">
            {report.totals.covered} of {report.totals.assets} catalogued stores covered by a feed;{' '}
            <strong>{report.totals.gaps} not covered</strong>.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Feed</th>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">{report.allUnits ? 'Stores covered' : 'Your stores covered'}</th>
                  <th className="text-left px-3 py-2">Last event</th>
                  <th className="text-left px-3 py-2">Expected every</th>
                  <th className="text-left px-3 py-2">PII elements</th>
                  <th className="text-left px-3 py-2">Send events to</th>
                </tr>
              </thead>
              <tbody>
                {report.feeds.map((f) => (
                  <tr key={f.id}>
                    <td className="px-3 py-1 font-mono text-xs">{f.sourceId}</td>
                    <td className="px-3 py-1">{VENDOR_LABEL[f.vendor] ?? f.vendor}</td>
                    <td className="px-3 py-1"><Badge variant={f.status === 'active' ? 'success' : 'info'}>{f.status}</Badge></td>
                    <td className="px-3 py-1 text-right tabular">{f.storesCovered}</td>
                    <td className="px-3 py-1 text-xs text-muted-foreground">
                      {when(f.lastEventAt)}{' '}
                      {f.stale && <span data-testid={`feed-stale-${f.id}`}><Badge variant="destructive">overdue</Badge></span>}
                      {f.heldBack && f.heldBack.count > 0 && (
                        <span
                          data-testid={`feed-held-back-${f.id}`}
                          title={`Delivered but not ingested and not being retried${f.heldBack.oldestAt ? `, oldest ${when(f.heldBack.oldestAt)}` : ''}. The collector stopped resending, or the activity pipeline keeps failing on them: check the collector and the controller log.`}
                        >
                          {' '}<Badge variant="destructive">{f.heldBack.atLeast ? 'at least ' : ''}{f.heldBack.count} held back</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1 text-xs">{interval(f.expectedIntervalMinutes)}</td>
                    <td className="px-3 py-1 text-xs" data-testid={`feed-pii-${f.id}`}>{piiElementsText(f.piiElements)}</td>
                    <td className="px-3 py-1 font-mono text-[11px]">POST /v1/activity/feeds/{f.id}/events</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report.stores.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="feed-coverage-stores">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Store</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Covered</th>
                    <th className="text-left px-3 py-2">By feed</th>
                    <th className="text-left px-3 py-2">PII elements</th>
                  </tr>
                </thead>
                <tbody>
                  {report.stores.map((s) => (
                    <tr key={s.assetId}>
                      <td className="px-3 py-1">{s.displayName}</td>
                      <td className="px-3 py-1 text-xs">{s.sourceType}</td>
                      <td className="px-3 py-1">
                        <Badge variant={s.covered ? 'success' : 'destructive'}>{s.covered ? 'covered' : 'stale'}</Badge>
                      </td>
                      <td className="px-3 py-1 text-xs">
                        {s.feeds.map((f) => `${f.sourceId} (${f.eventsTotal} events, last ${when(f.lastEventAt)})`).join('; ')}
                      </td>
                      <td className="px-3 py-1 text-xs">{piiElementsText(s.piiElements)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.gaps.items.length > 0 && (
            <div data-testid="feed-coverage-gaps">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Not covered by any feed ({report.totals.gaps})
              </h3>
              <ul className="text-sm columns-2">
                {report.gaps.items.map((g) => (
                  <li key={g.assetId}>{g.displayName} <span className="text-xs text-muted-foreground">({g.sourceType})</span></li>
                ))}
              </ul>
              {report.gaps.truncated && (
                <p className="text-xs text-muted-foreground mt-1">Showing the first {report.gaps.items.length}.</p>
              )}
            </div>
          )}

          {report.unmatched.hiddenForScope ? (
            <p className="text-xs text-muted-foreground" data-testid="feed-coverage-unmatched-hidden">
              {UNMATCHED_HIDDEN_TEXT}
            </p>
          ) : (
            report.unmatched.items.length > 0 && (
              <div data-testid="feed-coverage-unmatched">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Feed events not ingested, by store name and cause ({report.unmatched.total})
                </h3>
                <ul className="text-sm">
                  {report.unmatched.items.map((u) => (
                    <li key={`${u.feedId}-${u.storeRef}-${u.reason}`} data-testid="feed-coverage-unmatched-item">
                      <code>{u.storeRef}</code> from {u.sourceId}: {u.eventsNotIngested ?? u.eventsTotal} of {u.eventsTotal} events not
                      ingested. {reasonText(u.reason)}
                    </li>
                  ))}
                </ul>
                {report.unmatched.truncated && (
                  <p className="text-xs text-muted-foreground mt-1">Showing the {report.unmatched.items.length} most recent.</p>
                )}
              </div>
            )
          )}

          {report.linksTruncated && (
            <p className="text-xs text-muted-foreground">
              The feeds have named more stores than one report reads; the oldest names are left out.
            </p>
          )}
        </>
      )}

      {report.ownSensors && <OwnSensorCoverage own={report.ownSensors} allUnits={report.allUnits} />}
    </section>
  );
}
